import { useState } from "react";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { KeyRound, ShieldCheck } from "lucide-react";

import { api } from "../lib/api.js";

/**
 * Sign in, or claim the control plane.
 *
 * On a fresh install there is nothing to sign in with, so this is also the
 * enrolment screen. Claiming requires the one-time bootstrap code from the server
 * log — without it, whoever loaded this page first would own the box.
 */
export function SignIn({ auth, onSignedIn }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [code, setCode] = useState("");
    const [label, setLabel] = useState("");
    /*
     * On by default: this is a private control plane behind a passkey, and being
     * asked for it twice a day is the thing the box is here to stop. Trust lasts
     * until it is revoked in Settings, and the operations worth a second thought
     * ask for the passkey again whatever it says.
     */
    const [remember, setRemember] = useState(true);

    const claim = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const options = await api.auth.registerOptions({ bootstrapCode: code.trim(), label: label.trim() || "operator" });
            const credential = await startRegistration({ optionsJSON: options });
            await api.auth.registerVerify({ credential, label: label.trim() || "operator", remember });
            onSignedIn();
        } catch (caught) {
            setError(caught.message);
        } finally {
            setBusy(false);
        }
    };

    const signIn = async () => {
        setBusy(true);
        setError(null);
        try {
            const options = await api.auth.loginOptions();
            const credential = await startAuthentication({ optionsJSON: options });
            await api.auth.loginVerify({ credential, remember });
            onSignedIn();
        } catch (caught) {
            setError(caught.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center px-4">
            <div className="card w-full max-w-sm p-6">
                <div className="mb-5 flex flex-col items-center gap-2 text-center">
                    <img src="/logo.svg" alt="" className="h-7 dark:hidden" />
                    <img src="/logo-white.svg" alt="" className="hidden h-7 dark:block" />
                    <h1 className="muted flex items-center gap-1.5 text-xs">
                        <ShieldCheck size={13} aria-hidden /> deploy control plane
                    </h1>
                </div>

                {auth.needsBootstrap ? (
                    <form onSubmit={claim} className="space-y-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            No passkey is enrolled yet. Enter the one-time bootstrap code from the server log
                            (<code className="font-mono text-xs">pm2 logs ops-api</code>) to claim this control plane.
                        </p>
                        <div>
                            <label className="label mb-1" htmlFor="code">Bootstrap code</label>
                            <input
                                id="code"
                                className="input font-mono"
                                value={code}
                                onChange={(event) => setCode(event.target.value)}
                                autoComplete="off"
                                required
                            />
                        </div>
                        <div>
                            <label className="label mb-1" htmlFor="label">Name this passkey</label>
                            <input
                                id="label"
                                className="input"
                                placeholder="MacBook"
                                value={label}
                                onChange={(event) => setLabel(event.target.value)}
                            />
                        </div>
                        <TrustDevice checked={remember} onChange={setRemember} />
                        <button type="submit" className="btn-primary w-full" disabled={busy}>
                            <KeyRound size={15} /> {busy ? "waiting for your passkey…" : "Create passkey"}
                        </button>
                    </form>
                ) : (
                    <div className="space-y-3">
                        <button onClick={signIn} className="btn-primary w-full" disabled={busy}>
                            <KeyRound size={15} /> {busy ? "waiting for your passkey…" : "Sign in with a passkey"}
                        </button>
                        <TrustDevice checked={remember} onChange={setRemember} />
                    </div>
                )}

                {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

                {auth.unreachable ? (
                    <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">The server is not responding.</p>
                ) : null}
            </div>
        </div>
    );
}

/** One line, because the explanation belongs where the trust is revoked. */
function TrustDevice({ checked, onChange }) {
    return (
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
                type="checkbox"
                className="size-4 shrink-0"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
            />
            Stay signed in on this device
        </label>
    );
}
